/**
 * EditableFieldsForm - Human-in-the-Loop Field Editing Component
 * Displays extracted fields from agent in editable form with validation
 */
import { useState, useEffect } from "react";

// ─── Field Type Input Components ─────────────────────────────────────────────

function CurrencyInput({ label, value, onChange, disabled, error, warning }) {
  const handleChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    onChange(val ? parseFloat(val) : null);
  };

  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-[#556B82] uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#556B82]">$</span>
        <input
          type="text"
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          className={`w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
            disabled
              ? 'bg-gray-50 text-gray-500 border-gray-200'
              : error
              ? 'border-red-300 focus:ring-red-200 bg-red-50'
              : warning
              ? 'border-amber-300 focus:ring-amber-200 bg-amber-50'
              : 'border-[#B8D6FF] focus:ring-[#B8D6FF]'
          }`}
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
    </div>
  );
}

function PercentageInput({ label, value, onChange, disabled, error, warning }) {
  const handleChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    onChange(val ? parseFloat(val) : null);
  };

  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-[#556B82] uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          className={`w-full pr-8 pl-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
            disabled
              ? 'bg-gray-50 text-gray-500 border-gray-200'
              : error
              ? 'border-red-300 focus:ring-red-200 bg-red-50'
              : warning
              ? 'border-amber-300 focus:ring-amber-200 bg-amber-50'
              : 'border-[#B8D6FF] focus:ring-[#B8D6FF]'
          }`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#556B82]">%</span>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
    </div>
  );
}

function TextInput({ label, value, onChange, disabled, error, warning }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-[#556B82] uppercase tracking-wider">
        {label}
      </label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
          disabled
            ? 'bg-gray-50 text-gray-500 border-gray-200'
            : error
            ? 'border-red-300 focus:ring-red-200 bg-red-50'
            : warning
            ? 'border-amber-300 focus:ring-amber-200 bg-amber-50'
            : 'border-[#B8D6FF] focus:ring-[#B8D6FF]'
        }`}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
    </div>
  );
}

function IntegerInput({ label, value, onChange, disabled, error, warning }) {
  const handleChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    onChange(val ? parseInt(val, 10) : null);
  };

  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-[#556B82] uppercase tracking-wider">
        {label}
      </label>
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
          disabled
            ? 'bg-gray-50 text-gray-500 border-gray-200'
            : error
            ? 'border-red-300 focus:ring-red-200 bg-red-50'
            : warning
            ? 'border-amber-300 focus:ring-amber-200 bg-amber-50'
            : 'border-[#B8D6FF] focus:ring-[#B8D6FF]'
        }`}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
    </div>
  );
}

// ─── Field Label Mapping ──────────────────────────────────────────────────────

const FIELD_LABELS = {
  doc_invoice_number: 'Invoice Number',
  doc_vendor_name: 'Vendor Name',
  po_number: 'PO Number',
  po_confirmation_statement: 'PO Confirmation',
  corrected_amount: 'Corrected Amount',
  corrected_unit_price: 'Corrected Unit Price',
  corrected_quantity: 'Corrected Quantity',
  corrected_gr_quantity: 'Corrected GR Quantity',
  gr_confirmation_number: 'GR Confirmation Number',
  freight_amount: 'Freight Amount',
  carrier_name: 'Carrier Name',
  carrier_rate_reference: 'Carrier Rate Reference',
  customs_amount: 'Customs Amount',
  customs_declaration_number: 'Customs Declaration Number',
  customs_jurisdiction: 'Customs Jurisdiction',
  fuel_surcharge_amount: 'Fuel Surcharge Amount',
  fuel_clause_reference: 'Fuel Clause Reference',
  applicable_period: 'Applicable Period',
  misc_charge_amount: 'Miscellaneous Charge Amount',
  charge_description: 'Charge Description',
  approval_authority: 'Approval Authority',
  corrected_tax_amount: 'Corrected Tax Amount',
  corrected_tax_rate: 'Corrected Tax Rate',
  tax_code: 'Tax Code',
  tax_jurisdiction: 'Tax Jurisdiction',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EditableFieldsForm({
  exceptionId,
  exceptionCode,
  extractedFields,
  fieldEditRules,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) {
  const [fields, setFields] = useState({});
  const [originalFields, setOriginalFields] = useState({});
  const [flags, setFlags] = useState({});
  const [comments, setComments] = useState('');
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});

  // Initialize fields from extracted data
  useEffect(() => {
    if (extractedFields) {
      const fieldValues = {};
      const origValues = {};
      Object.entries(extractedFields).forEach(([key, fieldData]) => {
        fieldValues[key] = fieldData.value;
        origValues[key] = fieldData.value;
      });
      setFields(fieldValues);
      setOriginalFields(origValues);
    }
  }, [extractedFields]);

  // Validate field based on rules
  const validateField = (fieldName, value) => {
    const rules = fieldEditRules?.validation?.[fieldName];
    if (!rules) return { valid: true };

    const newErrors = {};
    const newWarnings = {};

    // Min validation
    if (rules.min !== undefined && value < rules.min) {
      newErrors[fieldName] = `Must be at least ${rules.min}`;
      return { valid: false, error: newErrors[fieldName] };
    }

    // Max validation
    if (rules.max !== undefined && value > rules.max) {
      newErrors[fieldName] = `Must be at most ${rules.max}`;
      return { valid: false, error: newErrors[fieldName] };
    }

    // Warning threshold (10% change from original)
    if (originalFields[fieldName] && typeof value === 'number' && typeof originalFields[fieldName] === 'number') {
      const changePercent = Math.abs((value - originalFields[fieldName]) / originalFields[fieldName] * 100);
      if (changePercent > 10) {
        newWarnings[fieldName] = `${changePercent.toFixed(1)}% change from original`;
      }
    }

    return { valid: true, warning: newWarnings[fieldName] };
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    setFields(prev => ({ ...prev, [fieldName]: value }));

    // Validate
    const { valid, error, warning } = validateField(fieldName, value);
    setErrors(prev => {
      const updated = { ...prev };
      if (error) updated[fieldName] = error;
      else delete updated[fieldName];
      return updated;
    });
    setWarnings(prev => {
      const updated = { ...prev };
      if (warning) updated[fieldName] = warning;
      else delete updated[fieldName];
      return updated;
    });

    // Handle calculated fields
    if (fieldEditRules?.calculated?.includes(fieldName)) {
      handleCalculatedFields(fieldName, value);
    }
  };

  // Handle calculated field dependencies
  const handleCalculatedFields = (changedField, value) => {
    // Example: If tax_rate changes, recalculate tax_amount
    if (changedField === 'corrected_tax_rate' && fields.corrected_amount) {
      const taxAmount = (fields.corrected_amount * value) / 100;
      setFields(prev => ({ ...prev, corrected_tax_amount: taxAmount }));
    }
    // Example: If unit_price or quantity changes, recalculate amount
    if ((changedField === 'corrected_unit_price' || changedField === 'corrected_quantity') &&
        fields.corrected_unit_price && fields.corrected_quantity) {
      const amount = fields.corrected_unit_price * fields.corrected_quantity;
      setFields(prev => ({ ...prev, corrected_amount: amount }));
    }
  };

  // Reset to original values
  const handleReset = () => {
    setFields({ ...originalFields });
    setErrors({});
    setWarnings({});
    setFlags({});
    setComments('');
  };

  // Handle flag toggle
  const handleFlagToggle = (flagName) => {
    setFlags(prev => ({ ...prev, [flagName]: !prev[flagName] }));
  };

  // Handle submit
  const handleSubmit = () => {
    // Check for blocking errors
    if (Object.keys(errors).length > 0) {
      alert('Please fix all errors before submitting.');
      return;
    }

    // Build edited fields payload
    const editedFields = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== originalFields[key]) {
        editedFields[key] = {
          original: originalFields[key],
          edited: value,
          validation_status: warnings[key] ? 'warning' : 'valid',
        };
      }
    });

    // Get active flags
    const activeFlags = Object.entries(flags)
      .filter(([_, checked]) => checked)
      .map(([name, _]) => name);

    onSubmit({
      edited_fields: editedFields,
      flags: activeFlags,
      comments: comments.trim() || null,
      approver: 'arjun.jagdale@karini.ai', // TODO: Get from auth context
    });
  };

  // Render field based on type
  const renderField = (fieldName, fieldData) => {
    const { type, editable } = fieldData;
    const label = FIELD_LABELS[fieldName] || fieldName;
    const value = fields[fieldName];
    const error = errors[fieldName];
    const warning = warnings[fieldName];
    const disabled = !editable;

    const props = {
      label,
      value,
      onChange: (val) => handleFieldChange(fieldName, val),
      disabled,
      error,
      warning,
    };

    switch (type) {
      case 'currency':
        return <CurrencyInput key={fieldName} {...props} />;
      case 'percentage':
        return <PercentageInput key={fieldName} {...props} />;
      case 'integer':
        return <IntegerInput key={fieldName} {...props} />;
      case 'text':
      default:
        return <TextInput key={fieldName} {...props} />;
    }
  };

  // Check if any changes were made
  const hasChanges = Object.entries(fields).some(
    ([key, value]) => value !== originalFields[key]
  ) || Object.values(flags).some(f => f) || comments.trim();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-700 to-violet-500 px-5 py-3 rounded-t-xl">
        <h3 className="text-sm font-bold uppercase tracking-widest text-white">
          Review & Edit Extracted Fields
        </h3>
        <p className="text-xs text-white/80 mt-1">
          Exception {exceptionCode} - Human-in-the-Loop Editing
        </p>
      </div>

      {/* Fields Grid */}
      <div className="px-5 grid grid-cols-2 gap-4">
        {extractedFields && Object.entries(extractedFields).map(([fieldName, fieldData]) =>
          renderField(fieldName, fieldData)
        )}
      </div>

      {/* Flags */}
      {fieldEditRules?.flags_available && fieldEditRules.flags_available.length > 0 && (
        <div className="px-5 space-y-2">
          <p className="text-xs font-bold text-[#556B82] uppercase tracking-wider mb-2">
            Additional Flags
          </p>
          {fieldEditRules.flags_available.map(flagName => (
            <label key={flagName} className="flex items-center gap-2 text-sm text-[#1D2D3E] cursor-pointer">
              <input
                type="checkbox"
                checked={!!flags[flagName]}
                onChange={() => handleFlagToggle(flagName)}
                className="w-4 h-4 text-[#0070F2] border-gray-300 rounded focus:ring-[#B8D6FF]"
              />
              <span>{flagName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
            </label>
          ))}
        </div>
      )}

      {/* Comments */}
      <div className="px-5 space-y-1">
        <label className="block text-xs font-bold text-[#556B82] uppercase tracking-wider">
          Comments (Optional)
        </label>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Add any notes about your edits..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-[#B8D6FF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B8D6FF]"
        />
      </div>

      {/* Actions */}
      <div className="px-5 pb-5 flex gap-3">
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasChanges || isSubmitting}
          className="flex-1 px-4 py-2 text-sm font-bold text-[#556B82] bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Reset to Original
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 text-sm font-bold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-red-200"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={Object.keys(errors).length > 0 || isSubmitting}
          className="flex-1 px-4 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {isSubmitting ? 'Submitting...' : hasChanges ? 'Approve with Edits' : 'Approve'}
        </button>
      </div>

      {/* Status Message */}
      {hasChanges && (
        <div className="px-5 pb-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>Changes detected:</strong> {Object.keys(fields).filter(k => fields[k] !== originalFields[k]).length} field(s) modified
            {Object.values(flags).some(f => f) && ', flags added'}
            {comments.trim() && ', comments added'}
          </div>
        </div>
      )}
    </div>
  );
}
